import { useSupabase } from "../hooks/useSupabase";
import { 
  GroupCategoryPage,
  PostCategory,
  LanguageCode, 
  TranslationInput 
} from "../types/post";

export const useGroupCategoryPageService = () => {
  const supabase = useSupabase();

  // Translation helper functions that use the same supabase instance
  const translationHelpers = {
    async getRecordTranslations(tableName: string, recordId: string) {
      const { data, error } = await supabase
        .from("translations")
        .select("*")
        .eq("table_name", tableName)
        .eq("record_id", recordId);
      
      if (error) throw error;
      return data || [];
    },

    async setRecordTranslations(tableName: string, recordId: string, translations: TranslationInput[]) {
      const promises = translations.map(translation =>
        supabase.rpc('set_translation', {
          p_table_name: tableName,
          p_record_id: recordId,
          p_field_name: translation.field_name,
          p_language_code: translation.language_code,
          p_value: translation.value
        })
      );
      
      await Promise.all(promises);
    },

    async deleteRecordTranslations(tableName: string, recordId: string) {
      const { error } = await supabase
        .from("translations")
        .delete()
        .eq("table_name", tableName)
        .eq("record_id", recordId);
      
      if (error) throw error;
    },

    formatTranslations(translations: any[]) {
      const result: Record<string, Record<LanguageCode, string>> = {};
      
      translations.forEach(translation => {
        if (!result[translation.field_name]) {
          result[translation.field_name] = {} as Record<LanguageCode, string>;
        }
        (result[translation.field_name] as any)[translation.language_code] = translation.value;
      });
      
      return result;
    },

    getLocalizedText(translations: Record<LanguageCode, string> | undefined, language: LanguageCode): string {
      if (!translations) return '';
      
      // Priority order: requested language, Polish (default), English, Ukrainian, any available
      const availableLanguages: LanguageCode[] = [language, 'pl', 'en', 'uk'];
      
      for (const lang of availableLanguages) {
        if (translations[lang]) {
          return translations[lang];
        }
      }
      
      return '';
    }
  };

  return {
    // Get all group category pages with their categories
    async getGroupCategoryPages(language: LanguageCode = 'pl'): Promise<GroupCategoryPage[]> {
      const { data, error } = await supabase
        .from("group_category_page")
        .select(`
          *,
          category_post (
            id,
            group_key,
            icon,
            color,
            group_category_page_id
          )
        `)
        .order("display_order", { ascending: true });
      
      if (error) throw error;
      
      // Get translations for all group category pages and their categories
      const groupPagesWithTranslations = await Promise.all(
        data.map(async (groupPage) => {
          // Get translations for the group page
          const groupTranslations = await translationHelpers.getRecordTranslations('group_category_page', groupPage.id);
          const formattedGroupTranslations = translationHelpers.formatTranslations(groupTranslations);
          
          // Get categories with translations
          const categoriesWithTranslations = await Promise.all(
            (groupPage.category_post || []).map(async (category: any) => {
              const categoryTranslations = await translationHelpers.getRecordTranslations('category_post', category.id);
              const formattedCategoryTranslations = translationHelpers.formatTranslations(categoryTranslations);
              
              return {
                ...category,
                name: translationHelpers.getLocalizedText(formattedCategoryTranslations.name, language),
                description: translationHelpers.getLocalizedText(formattedCategoryTranslations.description, language),
                translations: formattedCategoryTranslations
              };
            })
          );
          
          return {
            ...groupPage,
            name: translationHelpers.getLocalizedText(formattedGroupTranslations.name, language),
            description: translationHelpers.getLocalizedText(formattedGroupTranslations.description, language),
            translations: formattedGroupTranslations,
            categories: categoriesWithTranslations
          };
        })
      );
      
      return groupPagesWithTranslations;
    },

    // Get a single group category page with its categories
    async getGroupCategoryPage(id: string, language: LanguageCode = 'pl'): Promise<GroupCategoryPage | null> {
      const { data, error } = await supabase
        .from("group_category_page")
        .select(`
          *,
          category_post (
            id,
            group_key,
            icon,
            color,
            group_category_page_id
          )
        `)
        .eq("id", id)
        .single();
      
      if (error) {
        if (error.code === "PGRST116") {
          return null; // Not found
        }
        throw error;
      }
      
      // Get translations for the group page
      const groupTranslations = await translationHelpers.getRecordTranslations('group_category_page', data.id);
      const formattedGroupTranslations = translationHelpers.formatTranslations(groupTranslations);
      
      // Get categories with translations
      const categoriesWithTranslations = await Promise.all(
        (data.category_post || []).map(async (category: any) => {
          const categoryTranslations = await translationHelpers.getRecordTranslations('category_post', category.id);
          const formattedCategoryTranslations = translationHelpers.formatTranslations(categoryTranslations);
          
          return {
            ...category,
            name: translationHelpers.getLocalizedText(formattedCategoryTranslations.name, language),
            description: translationHelpers.getLocalizedText(formattedCategoryTranslations.description, language),
            translations: formattedCategoryTranslations
          };
        })
      );
      
      return {
        ...data,
        name: translationHelpers.getLocalizedText(formattedGroupTranslations.name, language),
        description: translationHelpers.getLocalizedText(formattedGroupTranslations.description, language),
        translations: formattedGroupTranslations,
        categories: categoriesWithTranslations
      };
    },

    // Create a new group category page with translations
    async createGroupCategoryPage(
      translations: { name: Record<LanguageCode, string>; description?: Record<LanguageCode, string> },
      slug?: string,
      icon?: string,
      color?: string,
      isPublic: boolean = false,
      displayOrder: number = 0
    ): Promise<GroupCategoryPage> {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      // Create the group category page
      const { data, error } = await supabase
        .from("group_category_page")
        .insert({ 
          slug, 
          icon, 
          color, 
          is_public: isPublic,
          user_id: user.id,
          display_order: displayOrder
        })
        .select()
        .single();
      
      if (error) throw error;
      
      // Add translations
      const translationInputs: TranslationInput[] = [];
      
      // Add name translations
      Object.entries(translations.name).forEach(([lang, value]) => {
        if (value.trim()) {
          translationInputs.push({
            field_name: 'name',
            language_code: lang as LanguageCode,
            value: value.trim()
          });
        }
      });
      
      // Add description translations if provided
      if (translations.description) {
        Object.entries(translations.description).forEach(([lang, value]) => {
          if (value.trim()) {
            translationInputs.push({
              field_name: 'description',
              language_code: lang as LanguageCode,
              value: value.trim()
            });
          }
        });
      }
      
      if (translationInputs.length > 0) {
        await translationHelpers.setRecordTranslations('group_category_page', data.id, translationInputs);
      }
      
      // Return with translations loaded
      const groupTranslationsData = await translationHelpers.getRecordTranslations('group_category_page', data.id);
      const groupTranslations = translationHelpers.formatTranslations(groupTranslationsData);
      
      return {
        ...data,
        name: translationHelpers.getLocalizedText(groupTranslations.name, 'pl'),
        description: translationHelpers.getLocalizedText(groupTranslations.description, 'pl'),
        translations: groupTranslations,
        categories: []
      };
    },

    // Update an existing group category page with translations
    async updateGroupCategoryPage(
      id: string,
      translations: { name: Record<LanguageCode, string>; description?: Record<LanguageCode, string> },
      slug?: string,
      icon?: string,
      color?: string,
      isPublic?: boolean,
      displayOrder?: number
    ): Promise<GroupCategoryPage> {
      // Update the group category page record
      const updateData: any = {};
      if (slug !== undefined) updateData.slug = slug;
      if (icon !== undefined) updateData.icon = icon;
      if (color !== undefined) updateData.color = color;
      if (isPublic !== undefined) updateData.is_public = isPublic;
      if (displayOrder !== undefined) updateData.display_order = displayOrder;

      const { data, error } = await supabase
        .from("group_category_page")
        .update(updateData)
        .eq("id", id)
        .select()
        .single();
      
      if (error) throw error;
      
      // Update translations
      const translationInputs: TranslationInput[] = [];
      
      // Add name translations
      Object.entries(translations.name).forEach(([lang, value]) => {
        translationInputs.push({
          field_name: 'name',
          language_code: lang as LanguageCode,
          value: value.trim()
        });
      });
      
      // Add description translations if provided
      if (translations.description) {
        Object.entries(translations.description).forEach(([lang, value]) => {
          if (value.trim()) {
            translationInputs.push({
              field_name: 'description',
              language_code: lang as LanguageCode,
              value: value.trim()
            });
          }
        });
      }
      
      if (translationInputs.length > 0) {
        await translationHelpers.setRecordTranslations('group_category_page', data.id, translationInputs);
      }
      
      // Return with translations loaded
      const groupTranslationsData = await translationHelpers.getRecordTranslations('group_category_page', data.id);
      const groupTranslations = translationHelpers.formatTranslations(groupTranslationsData);
      
      return {
        ...data,
        name: translationHelpers.getLocalizedText(groupTranslations.name, 'pl'),
        description: translationHelpers.getLocalizedText(groupTranslations.description, 'pl'),
        translations: groupTranslations
      };
    },

    // Delete a group category page
    async deleteGroupCategoryPage(id: string): Promise<void> {
      // Delete translations first
      await translationHelpers.deleteRecordTranslations('group_category_page', id);
      
      // The categories will have their group_category_page_id set to NULL due to ON DELETE SET NULL
      // Delete the group category page
      const { error } = await supabase
        .from("group_category_page")
        .delete()
        .eq("id", id);
      
      if (error) throw error;
    },

    // Assign categories to a group category page
    async assignCategoriesToGroupPage(groupPageId: string, categoryIds: string[]): Promise<void> {
      const { error } = await supabase
        .from("category_post")
        .update({ group_category_page_id: groupPageId })
        .in("id", categoryIds);
      
      if (error) throw error;
    },

    // Remove categories from a group category page
    async removeCategoriesFromGroupPage(categoryIds: string[]): Promise<void> {
      const { error } = await supabase
        .from("category_post")
        .update({ group_category_page_id: null })
        .in("id", categoryIds);
      
      if (error) throw error;
    },

    // Get categories not assigned to any group page
    async getUnassignedCategories(language: LanguageCode = 'pl'): Promise<PostCategory[]> {
      const { data, error } = await supabase
        .from("category_post")
        .select("*")
        .is("group_category_page_id", null);
      
      if (error) throw error;
      
      // Get translations for all categories
      const categoriesWithTranslations = await Promise.all(
        data.map(async (category) => {
          const translations = await translationHelpers.getRecordTranslations('category_post', category.id);
          const formattedTranslations = translationHelpers.formatTranslations(translations);
          
          return {
            ...category,
            name: translationHelpers.getLocalizedText(formattedTranslations.name, language),
            description: translationHelpers.getLocalizedText(formattedTranslations.description, language),
            translations: formattedTranslations
          };
        })
      );
      
      return categoriesWithTranslations;
    }
  };
};
